// src/pages/Tenant/AppLogins/AppLogins.jsx
import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import CustomExport from "../../../components/CustomExport/CustomExport";
import { useFetchLoginHistoryMutation } from "../../../features/tenant/AppLogins/appLoginsAPI";


/* --------------------------------------------------
 * 2️⃣  Component
 * -------------------------------------------------- */
const AppLogins = () => {
    // const navigate = useNavigate();

    const initialRequest = {
        mobile: "",
        user_id : "",
        name : "",
        zone_name : "",
        limit: 10,
        offset: 0,
        startDate: "",
        endDate: "",
        state : ""
    };
    const [request, setRequest] = useState(initialRequest);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);

    const clearFilters = () => {
        setRequest(initialRequest);
        fetchData(initialRequest);
    }

    /* ---------- API calls ---------- */
    const [
        fetchLogins,
        { data: loginsData, error: loginsError, isLoading: loginsLoading },
    ] = useFetchLoginHistoryMutation();

    /* ---------- state for table ---------- */
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    /* ---------- initial + filter fetch ---------- */
    useEffect(() => {
        if (request.mobile == "") {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit, offset]);

    /* ---------- pagination handlers ---------- */
    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
        setRequest((prevRequest) => ({ ...prevRequest, offset: newOffset }));
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage) => {
        setLimit(newPerPage);
        setOffset(0);
        setCurrentPage(1);
        setPaginationResetToggle((prev) => !prev);
        setRequest((prevRequest) => ({
          ...prevRequest,
          limit: newPerPage,
          offset: 0,
        }));
    };

    /* ---------- side-effects for API data ---------- */
    useEffect(() => {
        if (loginsData && loginsData?.body) {
            setRows(loginsData.body?.res);
            setTotalRows(loginsData.body?.total)
        }
    }, [loginsData, loginsError]);

    /* ---------- CSV export ---------- */
    const [csvData, setCsvData] = useState(null);
    const [fetchLoginsCsv,

        { data: loginsCsvData, error: loginsCsvError, isLoading: loginsCsvLoading },
    ] = useFetchLoginHistoryMutation(); // same endpoint, no limit/offset

    const fetchCsv = () => {
        fetchLoginsCsv({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: { ...request, limit: null, offset: null },
        })
    };


    const fetchData = (defaultRequest = null) => {
        const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
        
        const myRequest = defaultRequest ? defaultRequest : request;
        let endDate;
        if (myRequest.endDate) {
          const asUtc = new Date(`${myRequest.endDate}T23:59:59.999Z`); 
          endDate = new Date(asUtc.getTime() - IST_OFFSET_MS);        
        }
      
        let startDate;
        if (myRequest.startDate) {
          const asUtc = new Date(`${myRequest.startDate}T00:00:00.000Z`);
          startDate = new Date(asUtc.getTime() - IST_OFFSET_MS);       
        }
      
        const newRequest = { ...myRequest };
      
        if (startDate) newRequest.startDate = startDate.toISOString();
        else delete newRequest.startDate;
      
        if (endDate) newRequest.endDate = endDate.toISOString();
        else delete newRequest.endDate;
      
        fetchLogins({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          body: newRequest,
        });
      };
      


    useEffect(() => {
        if (loginsCsvData) {
            setCsvData(loginsCsvData.body?.res);
        }
    }, [loginsCsvData, loginsCsvError])



    const columns = [
        {
            name: "S.No",
            cell: (_row, idx) => offset + idx + 1,
            sortable: true,
            width: "90px",
        },
        {
            name: "Name",
            selector: (row) => row.name || "N/A",
            sortable: true,
            width: "150px",
        },
        {
            name: "Mobile",
            selector: (row) => row.mobile || "N/A",
            sortable: true,
            width: "120px",
        },
        {
            name: "User ID",
            selector: (row) => row.user_id || "N/A",
            sortable: true,
            width: "120px",
        },
        {
            name: "Zone Name",
            selector: (row) => row.zone_name || "N/A",
            sortable: true,
            width: "120px",
        },
        {
            name: "State",
            selector: (row) => row.state || "N/A",
            sortable: true,
            width: "100px",
        },
        {
            name: "District",
            selector: (row) => row.district || "N/A",
            sortable: true,
            width: "120px",
        },
        {
            name: "Platform",
            selector: (row) => row.platform || "N/A",
            sortable: true,
            width: "100px",
        },
        {
            name: "Initial Login",
            selector: (row) => row.initial_login ? new Date(row.initial_login).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Initial Login Address",
            selector: (row) => row.initial_address ? row.initial_address : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Login Date",
            selector: (row) => row.login_date ? new Date(row.login_date).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Login Count",
            selector: (row) => row.count_of_login || 0,
            sortable: true,
            width: "120px",
        },
        {
            name: "1st Login",
            selector: (row) => row.first_login_time ? new Date(row.first_login_time).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "2nd Login",
            selector: (row) => row.second_login_time ? new Date(row.second_login_time).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "3rd Login",
            selector: (row) => row.third_login_time ? new Date(row.third_login_time).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "4th Login",
            selector: (row) => row.fourth_login_time ? new Date(row.fourth_login_time).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "5th Login",
            selector: (row) => row.fifth_login_time ? new Date(row.fifth_login_time).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "6th Login",
            selector: (row) => row.sixth_login_time ? new Date(row.sixth_login_time).toLocaleString() : "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "1st Login Address",
            selector: (row) => row.first_login_address ? (row.first_login_address) : "N/A",
            sortable: true,
            width: "200px",
        },
        {
            name: "2nd Login Address",
            selector: (row) => row.second_login_address ? (row.second_login_address) : "N/A",
            sortable: true,
            width: "200px",
        },
        {
            name: "3rd Login Address",
            selector: (row) => row.third_login_address ? (row.third_login_address) : "N/A",
            sortable: true,
            width: "200px",
        },
    ];


    return (
        <div className="flex w-full bg-gray-200 h-full justify-center">
            <div className="w-full">
                {/* ---------- header ---------- */}
                <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                    User Login History
                    <LinksComponent />
                </div>

                <div className="p-3">
                    {/* ---------- export ---------- */}
                    <div className="mb-2">
                        <CustomExport
                            fetchData={fetchCsv}
                            data={csvData}
                            filename="app_logins"
                            defaultColumnExport={[
                                "mobile",
                                "platform",
                                "user_type",
                                "created_at",
                            ]}
                        />
                    </div>

                    {/* ---------- filters ---------- */}
                    <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
                        <div className="flex space-x-2 justify-end">
                            {/* mobile search */}

                            <div className="w-44 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        Mobile
                                    </label>
                                    <input
                                        type="number"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="Mobile No..."
                                        value={request.mobile}
                                        onChange={(e) =>
                                            setRequest({ ...request, mobile: e.target.value })
                                        }
                                    />
                                </div>
                            </div>


                            <div className="w-44 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="Name..."
                                        value={request.name}
                                        onChange={(e) =>
                                            setRequest({ ...request, name: e.target.value })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="w-44 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        User Id
                                    </label>
                                    <input
                                        type="text"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="User ID..."
                                        value={request.user_id}
                                        onChange={(e) =>
                                            setRequest({ ...request, user_id: e.target.value })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="w-44 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        Zone Name
                                    </label>
                                    <input
                                        type="text"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="Zone Name..."
                                        value={request.zone_name}
                                        onChange={(e) =>
                                            setRequest({ ...request, zone_name: e.target.value })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="w-44 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="State..."
                                        value={request.state}
                                        onChange={(e) =>
                                            setRequest({ ...request, state: e.target.value })
                                        }
                                    />
                                </div>
                            </div>


                            <div className="w-32 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="Start Date"
                                        value={request.startDate}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setRequest((prevState) => {
                                                return {
                                                    ...prevState,
                                                    startDate: newValue,
                                                };
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="w-44 h-10 ">
                                <div className="relative searchin">
                                    <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        id="default-search"
                                        className="w-full px-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                                        placeholder="End Date"
                                        value={request.endDate}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setRequest((prevState) => {
                                                return {
                                                    ...prevState,
                                                    endDate: newValue,
                                                };
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {/* buttons */}
                            <div className="flex gap-2 items-end">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => {
                                        fetchData();
                                    }}
                                >
                                    Search
                                </button>
                                <button
                                    className="btn btn-outline-primary whitespace-nowrap"
                                    onClick={clearFilters}
                                >
                                    Clear Filter
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ---------- table ---------- */}
                    <div className="p-3 w-full bg-white shadow rounded-lg">
                        <DataTableComponent
                            keyField={(row) => row.app_user_id}
                            columns={columns}
                            data={rows}
                            loading={loginsLoading}
                            totalRows={totalRows}
                            handlePerRowsChange={handlePerRowsChange}
                            handlePageChange={handlePageChange}
                            paginationDefaultPage={currentPage}
                            paginationResetDefaultPage={paginationResetToggle}
                            striped
                            highlightOnHover
                            pointerOnHover
                            pagination
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppLogins;
